// components/patient/PrescriptionList.jsx
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { DB } from '../../utils/db';

export function PrescriptionList({ user, onNavigate }) {
  const { brand } = useBrand();
  const [prescriptions, setPrescriptions] = useState([]);
  const [filter, setFilter] = useState('all');
  
  useEffect(() => {
    loadPrescriptions();
  }, [user.id]);
  
  const loadPrescriptions = async () => {
    const allRxs = await DB.get('d2p_prescriptions') || [];
    const userRxs = allRxs.filter(rx => rx.userId === user.id);
    setPrescriptions(userRxs);
  };
  
  const filteredRxs = filter === 'all' 
    ? prescriptions 
    : prescriptions.filter(rx => rx.status === filter);
  
  const filterOptions = [
    { value: 'all', label: 'All', count: prescriptions.length },
    { value: 'verified', label: 'Verified', count: prescriptions.filter(rx => rx.status === 'verified').length },
    { value: 'pending_review', label: 'Pending', count: prescriptions.filter(rx => rx.status === 'pending_review').length }
  ];
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          marginBottom: 32,
          flexWrap: 'wrap',
          gap: 16
        }}>
          <div>
            <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
              My Prescriptions
            </h1>
            <p style={{ color: '#6B7280' }}>
              Manage and track all your medications
            </p>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button icon="upload" onClick={() => onNavigate('upload')}>
              Upload New
            </Button>
            <Button variant="accent" icon="package" onClick={() => onNavigate('bundle')}>
              Bundle Order
            </Button>
          </div>
        </div>
        
        {/* Filter Tabs */}
        <div style={{
          display: 'flex',
          gap: 8,
          marginBottom: 24,
          borderBottom: '2px solid #E5E7EB',
          overflowX: 'auto'
        }}>
          {filterOptions.map(option => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'transparent',
                borderBottom: filter === option.value ? `3px solid ${brand.c.accent}` : '3px solid transparent',
                marginBottom: -2,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14,
                color: filter === option.value ? brand.c.accent : '#6B7280',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              {option.label}
              <span style={{
                marginLeft: 8,
                padding: '2px 8px',
                borderRadius: 12,
                background: filter === option.value ? brand.c.accent : '#E5E7EB',
                color: filter === option.value ? 'white' : '#6B7280',
                fontSize: 12,
                fontWeight: 700
              }}>
                {option.count}
              </span>
            </button>
          ))}
        </div>
        
        {/* Prescriptions List */}
        {filteredRxs.length === 0 ? (
          <Card>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Icon name="clipboard" size={40} color="#9CA3AF" />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
                {filter === 'all' ? 'No Prescriptions Yet' : `No ${filterOptions.find(f => f.value === filter)?.label} Prescriptions`}
              </h3>
              <p style={{ color: '#6B7280', marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
                {filter === 'all' 
                  ? 'Upload your first prescription to get started with affordable medications'
                  : `You don't have any prescriptions with this status`
                }
              </p>
              {filter === 'all' && (
                <Button icon="upload" onClick={() => onNavigate('upload')}>
                  Upload Prescription
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <div style={{ display: 'grid', gap: 16 }}>
            {filteredRxs.map(rx => (
              <Card key={rx.id} hover onClick={() => onNavigate('rx-detail', rx.id)}>
                <div style={{ display: 'flex', gap: 20, alignItems: 'start' }}>
                  {/* Icon */}
                  <div style={{
                    width: 56,
                    height: 56,
                    borderRadius: 12,
                    background: `${brand.c.accent}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Icon name="clipboard" size={28} color={brand.c.accent} />
                  </div>
                  
                  {/* Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 8,
                      flexWrap: 'wrap'
                    }}>
                      <h3 style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: brand.c.navy,
                        margin: 0
                      }}>
                        {rx.medication}
                      </h3>
                      <Badge status={rx.status} />
                    </div>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: 12,
                      fontSize: 14,
                      color: '#6B7280'
                    }}>
                      <div>
                        <strong style={{ color: '#374151' }}>Prescriber:</strong> {rx.prescriber}
                      </div>
                      <div>
                        <strong style={{ color: '#374151' }}>Quantity:</strong> {rx.quantity || 'Not specified'}
                      </div>
                      {rx.refills !== undefined && (
                        <div>
                          <strong style={{ color: '#374151' }}>Refills:</strong> {rx.refills} remaining
                        </div>
                      )}
                      <div>
                        <strong style={{ color: '#374151' }}>Uploaded:</strong> {new Date(rx.uploadedAt).toLocaleDateString()}
                      </div>
                    </div>
                    
                    {rx.status === 'verified' && rx.refills > 0 && (
                      <div style={{
                        marginTop: 12,
                        padding: '8px 12px',
                        background: '#D1FAE5',
                        borderRadius: 6,
                        fontSize: 13,
                        color: '#065F46',
                        display: 'inline-block'
                      }}>
                        ✓ Ready to order
                      </div>
                    )}
                  </div>
                  
                  {/* Arrow */}
                  <Icon name="arrowRight" size={20} color="#9CA3AF" style={{ flexShrink: 0 }} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
